// A move / turn gizmo for something in the scene (the editor's alignment tree nodes): arrows along
// x, y and z, a centre dot that moves across the horizontal plane, and a ring handle that turns
// about the vertical through the centre. Shift drags ten times finer. The move it makes, W (a turn
// about the original centre, then a shift), is reported live (change) for a preview, and given to
// apply on Apply / Enter, or dropped on Cancel / Esc. HTML handles over the canvas, like the
// volume box handles (volumeui.ts), with the axes and the ring drawn as viewer lines.
// As a square (square: true, for a point, which has no turn of its own) it only moves: its lines run
// long both ways, and a ring per axis turns them (and the arrows along them) about that axis, so it
// can be laid along a wall, an edge or a slope and the point moved square to it. Level stands it
// upright again. The square's turn is kept for the next.
import type { Viewer } from './viewer.ts';
import { type V3, add, scale, sub, dot, cross, norm, rotate, project } from './math.ts';
import { type Rigid, yawAbout } from './align.ts';

const AXES: { a: V3; color: [number, number, number, number]; css: string }[] = [
  { a: [1, 0, 0], color: [1, 0.3, 0.3, 1], css: '#ff5555' },
  { a: [0, 1, 0], color: [0.35, 0.9, 0.35, 1], css: '#5be05b' },
  { a: [0, 0, 1], color: [0.4, 0.6, 1, 1], css: '#6699ff' },
];
const RING = 48;               // segments of the turn ring
const LONG = 60;               // half length of a square's lines (m)

/** apply also gets the move as made: the centre's shift (m) and the turn (degrees), for a label. */
export interface GizmoHooks { change: (W: Rigid) => void; apply: (W: Rigid, made: { shift: V3; yawDeg: number }) => void; cancel: () => void }

type Drag =
  | { t: 'axis'; k: number; x0: number; y0: number; d0: V3; sv: [number, number] }
  | { t: 'plane'; z: number; p0: V3; d0: V3 }
  | { t: 'turn'; a0: number; yaw0: number }
  | { t: 'tilt'; k: number; a0: number; ax0: V3[] };

type Axes = [V3, V3, V3];
const UPRIGHT: Axes = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];

export class Gizmo {
  private d: V3 = [0, 0, 0];      // shift (m)
  private yaw = 0;                // turn (radians) about the vertical through c
  private box: HTMLElement;
  private bar: HTMLElement;
  private readout: HTMLElement;
  private handles: HTMLElement[] = [];
  private drag: Drag | null = null;
  /** The square's axes, as the last square left them. */
  private static squareAxes: Axes = UPRIGHT;
  private square: boolean;
  /** A square's axes now (turned by its rings), and as it started. */
  private ax: Axes = UPRIGHT;
  private axStart: Axes = UPRIGHT;
  private raf = 0;
  private linesKey = '';
  private done = false;
  private keys = (e: KeyboardEvent) => {
    if ((e.target as HTMLElement).closest?.('input,select,textarea')) return;
    if (e.code === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); this.cancel(); }
    if (e.code === 'Enter') { e.preventDefault(); this.apply(); }
  };

  constructor(private v: Viewer, private c: V3, title: string, private hooks: GizmoHooks, opts: { square?: boolean } = {}) {
    this.square = !!opts.square;
    if (this.square) this.ax = this.axStart = Gizmo.squareAxes;
    this.box = document.createElement('div');
    this.box.className = 'gizmo';
    const mk = (cls: string, color: string, tip: string, i: number) => {
      const h = document.createElement('div');
      h.className = 'vh gz ' + cls;
      h.style.background = color;
      h.title = tip;
      h.addEventListener('pointerdown', e => this.down(e, i));
      h.addEventListener('pointermove', e => this.move(e));
      h.addEventListener('pointerup', () => { this.drag = null; });
      h.addEventListener('pointercancel', () => { this.drag = null; });
      this.box.append(h);
      this.handles.push(h);
    };
    AXES.forEach((ax, i) => mk('axis', ax.css, `drag to move along ${'xyz'[i]}${this.square && i < 2 ? ' (its long line)' : ''} (shift: finer)`, i));
    mk('move', '#ffffff', 'drag to move across the floor plane (shift: finer)', 3);
    if (!this.square) mk('rot', '#000c', 'drag to turn about the vertical (shift: finer)', 4);
    else for (const k of [2, 0, 1]) {
      mk('rot', '#000c', `drag to turn the square (its long lines) about its ${'xyz'[k]} line (the ${['red', 'green', 'blue'][k]} ring), to lie along a wall, an edge or a slope; it does not turn the point (shift: finer)`, 4 + (k + 1) % 3);
      this.handles[this.handles.length - 1].style.borderColor = AXES[k].css;
    }
    for (const h of this.handles.slice(4)) h.textContent = '⟳';
    // the bar: what it does now, and Apply / Reset / Cancel
    this.bar = document.createElement('div');
    this.bar.className = 'gizmo-bar';
    const b = (label: string, tip: string, f: () => void) => { const x = document.createElement('button'); x.className = 'small'; x.textContent = label; x.title = tip; x.onclick = f; return x; };
    const t = document.createElement('b');
    t.textContent = title;
    this.readout = document.createElement('span');
    this.readout.className = 'mono';
    this.bar.append(t, this.readout, b('Apply', 'keep this move (Enter); it is one edit, undoable', () => this.apply()),
      b('Reset', 'back to where it started', () => { this.d = [0, 0, 0]; this.yaw = 0; this.ax = this.axStart; this.changed(); }),
      b('Cancel', 'drop it (Esc)', () => this.cancel()));
    if (this.square) this.bar.insertBefore(b('Level', 'stand the square upright: its blue line vertical, its red one keeping its heading', () => { this.ax = this.level(); this.changed(); }), this.bar.lastChild);
    document.body.append(this.box, this.bar);
    addEventListener('keydown', this.keys, true);
    this.changed();
    const tick = () => { this.place(); this.raf = requestAnimationFrame(tick); };
    tick();
  }

  /** The move so far. */
  get W(): Rigid { return yawAbout(this.square ? 0 : this.yaw, this.c, this.d); }
  private get centre(): V3 { return add(this.c, this.d); }

  /** Axis i as the gizmo has it now: a square's turn with it. */
  private axis(i: number): V3 { return this.square ? this.ax[i] : AXES[i].a; }

  /** The square upright: z up, x on its heading (or y's, if x points up or down). */
  private level(): Axes {
    const [x, y] = this.ax, h = Math.hypot(x[0], x[1]) > 1e-3 ? norm([x[0], x[1], 0]) : norm(cross(norm([y[0], y[1], 0]), [0, 0, 1]));
    return [h, cross([0, 0, 1], h), [0, 0, 1]];
  }

  /** A square's heading (x's, degrees) and tilt (z from the vertical, degrees). */
  private pose() {
    const [x, , z] = this.ax;
    return { heading: (Math.atan2(x[1], x[0]) * 180) / Math.PI, tilt: (Math.acos(Math.max(-1, Math.min(1, z[2]))) * 180) / Math.PI };
  }

  /** The turn handles' rings: per handle, the axis it turns about and the two it turns (u then w, u × w = the axis). */
  private rings(): { k: number; u: V3; w: V3 }[] {
    if (!this.square) {
      const ra = this.yaw + Math.PI / 4;   // the ⟳ sits at 45°, its ring flat
      return [{ k: 2, u: [Math.cos(ra), Math.sin(ra), 0], w: [-Math.sin(ra), Math.cos(ra), 0] }];
    }
    return [2, 0, 1].map(k => ({ k, u: this.ax[(k + 1) % 3], w: this.ax[(k + 2) % 3] }));
  }

  /** Handle size in metres: about 90 px on screen at the centre. */
  private len() {
    const cam = this.v.cam, depth = Math.max(0.2, dot(sub(this.centre, cam.e), cam.f));
    return 90 * (cam.ortho ? cam.ps : (2 * Math.tan(cam.fov / 2) * depth) / cam.h);
  }

  private screen(p: V3): [number, number] | null {
    const c = this.v.cam, q = project(c.vp, p);
    return q[3] > 0 && Math.abs(q[2]) <= 1 ? [((q[0] + 1) / 2) * c.w, ((1 - q[1]) / 2) * c.h] : null;
  }

  /** Per frame: the handles where the gizmo is, and its lines. */
  private place() {
    const r = this.v.canvas.getBoundingClientRect(), o = this.centre, L = this.len();
    const put = (h: HTMLElement, p: V3) => {
      const s = this.screen(p);
      h.style.display = s ? '' : 'none';
      if (s) h.style.transform = `translate(${s[0] + r.left}px,${s[1] + r.top}px)`;
    };
    AXES.forEach((_, i) => put(this.handles[i], add(o, scale(this.axis(i), L))));
    put(this.handles[3], o);
    const rings = this.rings();
    rings.forEach(({ u, w }, j) => put(this.handles[4 + j], add(o, scale(add(u, w), (L * 0.8) / Math.SQRT2))));
    // lines: the axes and the rings (drawn over the cloud), redrawn only when they change
    const key = `${o.map(x => x.toFixed(4))}|${L.toFixed(4)}|${this.square ? this.ax.flat().map(x => x.toFixed(5)) : ''}`;
    if (key === this.linesKey) return;
    this.linesKey = key;
    const seg: number[] = [];
    for (let i = 0; i < 3; i++) seg.push(...o, ...add(o, scale(this.axis(i), L)));
    this.v.setLines('gizmo:axes', new Float32Array(seg), [1, 1, 1, 0.9], true, false);
    if (this.square) {
      // the long lines, each its axis' colour, through the centre both ways (the vertical one shorter)
      const ln: number[] = [], col: number[] = [];
      for (let i = 0; i < 3; i++) {
        const u = scale(this.axis(i), i === 2 ? LONG / 10 : LONG);
        ln.push(...sub(o, u), ...add(o, u));
        const [r, g, b] = AXES[i].color;
        col.push(r, g, b, 0.75, r, g, b, 0.75);
      }
      this.v.setLines('gizmo:square', new Float32Array(ln), [1, 1, 1, 0.75], true, false, null, new Float32Array(col));
    }
    // a square's rings in their axis' colour (the one they turn about), a plain gizmo's in amber
    const ring: number[] = [], rc: number[] = [], R = L * 0.8;
    for (const { k, u, w } of rings) {
      const at = (a: number) => add(o, add(scale(u, Math.cos(a) * R), scale(w, Math.sin(a) * R)));
      const [r, g, b] = this.square ? AXES[k].color : [1, 0.85, 0.3];
      for (let i = 0; i < RING; i++) {
        ring.push(...at((2 * Math.PI * i) / RING), ...at((2 * Math.PI * (i + 1)) / RING));
        rc.push(r, g, b, 0.9, r, g, b, 0.9);
      }
    }
    this.v.setLines('gizmo:ring', new Float32Array(ring), [1, 0.85, 0.3, 0.9], true, false, null, new Float32Array(rc));
  }

  private xy(e: PointerEvent): [number, number] {
    const r = this.v.canvas.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  }

  /** Where the pointer's ray meets the horizontal plane at height z. */
  private onPlane(x: number, y: number, z: number): V3 | null {
    const { o, d } = this.v.cam.ray(x, y);
    if (Math.abs(d[2]) < 1e-4) return null;
    const t = (z - o[2]) / d[2];
    return t < 0 ? null : [o[0] + d[0] * t, o[1] + d[1] * t, z];
  }

  private down(e: PointerEvent, i: number) {
    if (e.button > 0) return;
    e.preventDefault(); e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const [x, y] = this.xy(e), o = this.centre;
    if (i < 3) {
      const p0 = this.screen(o), p1 = this.screen(add(o, scale(this.axis(i), 0.01)));
      if (!p0 || !p1) return;
      this.drag = { t: 'axis', k: i, x0: x, y0: y, d0: [...this.d] as V3, sv: [(p1[0] - p0[0]) / 0.01, (p1[1] - p0[1]) / 0.01] };
    } else if (i === 3) {
      const p0 = this.onPlane(x, y, o[2]);
      if (p0) this.drag = { t: 'plane', z: o[2], p0, d0: [...this.d] as V3 };
    } else if (!this.square) {
      const p = this.onPlane(x, y, o[2]);
      if (p) this.drag = { t: 'turn', a0: Math.atan2(p[1] - o[1], p[0] - o[0]), yaw0: this.yaw };
    } else {
      const { k } = this.rings()[i - 4], a0 = this.ringAngle(x, y, k, this.ax);
      if (a0 !== null) this.drag = { t: 'tilt', k, a0, ax0: [...this.ax] };
    }
  }

  /** Where the pointer is round ring k of axes ax (radians, about ax[k] from ax[k+1]), or null with the ring edge-on. */
  private ringAngle(x: number, y: number, k: number, ax: V3[]): number | null {
    const { o, d } = this.v.cam.ray(x, y), n = ax[k], c = this.centre, dn = dot(d, n);
    if (Math.abs(dn) < 0.05) return null;
    const t = dot(sub(c, o), n) / dn;
    if (t < 0) return null;
    const p = sub(add(o, scale(d, t)), c);
    return Math.atan2(dot(p, ax[(k + 2) % 3]), dot(p, ax[(k + 1) % 3]));
  }

  private move(e: PointerEvent) {
    const g = this.drag;
    if (!g) return;
    const [x, y] = this.xy(e), fine = e.shiftKey ? 0.1 : 1;
    if (g.t === 'axis') {
      // metres along the axis = mouse motion projected on the axis' screen direction
      const n2 = g.sv[0] ** 2 + g.sv[1] ** 2;
      if (n2 < 1e-6) return;
      const t = (((x - g.x0) * g.sv[0] + (y - g.y0) * g.sv[1]) / n2) * fine;
      this.d = add(g.d0, scale(this.axis(g.k), t));
    } else if (g.t === 'plane') {
      const p = this.onPlane(x, y, g.z);
      if (!p) return;
      const m = scale(sub(p, g.p0), fine);
      this.d = [g.d0[0] + m[0], g.d0[1] + m[1], g.d0[2]];
    } else if (g.t === 'tilt') {
      const a = this.ringAngle(x, y, g.k, g.ax0);
      if (a === null) return;
      let da = a - g.a0;
      da = ((da + 3 * Math.PI) % (2 * Math.PI)) - Math.PI;
      const n = g.ax0[g.k];
      this.ax = g.ax0.map(v => norm(rotate(v, n, da * fine))) as Axes;
    } else {
      const o = this.centre, p = this.onPlane(x, y, o[2]);
      if (!p) return;
      let da = Math.atan2(p[1] - o[1], p[0] - o[0]) - g.a0;
      da = ((da + 3 * Math.PI) % (2 * Math.PI)) - Math.PI;
      this.yaw = g.yaw0 + da * fine;
    }
    this.changed();
  }

  private changed() {
    const cm = this.d.map(x => (x * 100).toFixed(1)).join(' '), deg = ((this.yaw * 180) / Math.PI).toFixed(2), sq = this.square && this.pose();
    this.readout.textContent = sq ? ` moved ${cm} cm, square at ${sq.heading.toFixed(2)}°${sq.tilt > 0.005 ? `, tilted ${sq.tilt.toFixed(2)}°` : ''} ` : ` moved ${cm} cm, turned ${deg}° `;
    this.hooks.change(this.W);
    this.v.dirty = true;
  }

  private close() {
    if (this.done) return;
    this.done = true;
    if (this.square) Gizmo.squareAxes = this.ax;
    cancelAnimationFrame(this.raf);
    removeEventListener('keydown', this.keys, true);
    this.box.remove(); this.bar.remove();
    this.v.removeLines('gizmo:');
  }
  apply() { if (this.done) return; const W = this.W, made = { shift: [...this.d] as V3, yawDeg: this.square ? 0 : (this.yaw * 180) / Math.PI }; this.close(); this.hooks.apply(W, made); }
  cancel() { if (this.done) return; this.close(); this.hooks.cancel(); }
}
